/**
 * Single source of truth for top-level navigation.
 *
 * Why: AppHeader and the command palette were maintaining separate, drifting
 * route lists. This file is the only place a new top-level section is added.
 */
import { House, FileText, Pulse, ListChecks, Brain, TreeStructure, CalendarCheck, BookOpen, Calculator, ChatText, Wind, Cube, ShieldCheck, Hourglass, GridNine, Wallet, Car } from "@phosphor-icons/react";
import type { ScopeNode } from "@/shared/components/command-palette/types";

export const ROUTES: ScopeNode[] = [
  { id: "home", parentId: null, title: "Home", icon: House, href: "/" },
  { id: "chat", parentId: null, title: "Chat", icon: ChatText, href: "/chat", keywords: ["conversation", "talk", "ai"] },
  { id: "capture", parentId: null, title: "Capture", icon: GridNine, href: "/capture", keywords: ["grid", "spawn", "parallel", "fan out"] },
  { id: "notes", parentId: null, title: "Notes", icon: FileText, href: "/notes", keywords: ["write", "capture", "ideas"] },
  { id: "tasks", parentId: null, title: "Tasks", icon: ListChecks, href: "/tasks", keywords: ["todo", "goals"] },
  { id: "finance", parentId: null, title: "Finance", icon: Wallet, href: "/finance", keywords: ["money", "spend", "cards", "transactions", "budget", "ledger"] },
  { id: "input-types", parentId: null, title: "Input Types", shortTitle: "Inputs", icon: Pulse, href: "/input-types", keywords: ["track", "log", "entries"] },
  { id: "meditation", parentId: null, title: "Meditation", icon: Wind, href: "/meditation" },
  { id: "srs", parentId: null, title: "Review", shortTitle: "SRS", icon: Brain, href: "/srs", keywords: ["srs", "spaced repetition", "flashcards"] },
  { id: "reading", parentId: null, title: "Reading", icon: BookOpen, href: "/reading", keywords: ["books", "library"] },
  { id: "mental-math", parentId: null, title: "Mental Math", icon: Calculator, href: "/mental-math" },
  { id: "nback", parentId: null, title: "N-Back", icon: Brain, href: "/nback", keywords: ["working memory"] },
  { id: "relational", parentId: null, title: "Relational Reasoning", shortTitle: "Relational", icon: TreeStructure, href: "/relational", keywords: ["rft", "smart", "logic", "reasoning"] },
  { id: "battery", parentId: null, title: "Check-in", icon: CalendarCheck, href: "/battery", keywords: ["battery", "transfer", "baseline", "matrix", "span", "reaction"] },
  { id: "driving", parentId: null, title: "Driving", icon: Car, href: "/driving", keywords: ["drives", "car", "trips", "commute"] },
  { id: "app-usage", parentId: null, title: "App Usage", icon: Hourglass, href: "/app-usage", keywords: ["screen time"] },
  { id: "app-blocking", parentId: null, title: "App Blocking", icon: ShieldCheck, href: "/app-blocking", keywords: ["focus", "block"] },
  { id: "admin", parentId: null, title: "Admin", icon: Cube, href: "/admin", keywords: ["control plane", "ops"] },
];

/**
 * Subset rendered in the top app header, in order.
 */
export const HEADER_NAV_IDS = [
  "chat",
  "capture",
  "notes",
  "tasks",
  "finance",
  "input-types",
  "meditation",
  "srs",
  "reading",
  "mental-math",
] as const;

export const HEADER_NAV = HEADER_NAV_IDS
  .map((id) => ROUTES.find((r) => r.id === id))
  .filter((r): r is ScopeNode => Boolean(r));
